import type { Ref } from "vue";
import type { VideoCallPanelOffset } from "~/utils/videoCalls/panelPosition";
import { clampVideoCallPanelOffset } from "~/utils/videoCalls/panelPosition";

export interface VideoCallPanelDragOptions {
  /** The element that actually moves. Its box drives viewport clamping. */
  element: Ref<HTMLElement | null>;
  /** Dragging is only offered at sizes where repositioning is useful. */
  enabled: Ref<boolean>;
}

/** How far one arrow-key press nudges the panel, for pointer-free repositioning. */
const KEYBOARD_STEP_PX = 24;

const ARROW_KEY_STEPS: Record<string, VideoCallPanelOffset> = {
  ArrowUp: { x: 0, y: -KEYBOARD_STEP_PX },
  ArrowDown: { x: 0, y: KEYBOARD_STEP_PX },
  ArrowLeft: { x: -KEYBOARD_STEP_PX, y: 0 },
  ArrowRight: { x: KEYBOARD_STEP_PX, y: 0 },
};

const ORIGIN: VideoCallPanelOffset = { x: 0, y: 0 };

/**
 * Free-floating drag for the call panel, constrained so the panel can never be
 * dropped outside the viewport — losing the hang-up control off-screen would
 * leave the user stuck in a call.
 *
 * The offset is a transform on top of the panel's anchored position, so the
 * anchor keeps owning layout and only the displacement lives here.
 */
export const useVideoCallPanelDrag = ({
  element,
  enabled,
}: VideoCallPanelDragOptions) => {
  const offset = ref<VideoCallPanelOffset>({ ...ORIGIN });
  const dragging = ref(false);

  let pointerId: number | null = null;
  let pointerOrigin: VideoCallPanelOffset = { ...ORIGIN };
  let offsetOrigin: VideoCallPanelOffset = { ...ORIGIN };

  const clamp = (next: VideoCallPanelOffset): VideoCallPanelOffset => {
    const target = element.value;
    if (!target || typeof window === "undefined") return next;

    const box = target.getBoundingClientRect();
    return clampVideoCallPanelOffset({
      offset: next,
      appliedOffset: offset.value,
      box,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    });
  };

  const reset = () => {
    offset.value = { ...ORIGIN };
  };

  const nudge = (step: VideoCallPanelOffset) => {
    offset.value = clamp({
      x: offset.value.x + step.x,
      y: offset.value.y + step.y,
    });
  };

  const stopDragging = () => {
    if (!dragging.value) return;
    dragging.value = false;
    pointerId = null;
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!dragging.value || event.pointerId !== pointerId) return;
    event.preventDefault();
    offset.value = clamp({
      x: offsetOrigin.x + (event.clientX - pointerOrigin.x),
      y: offsetOrigin.y + (event.clientY - pointerOrigin.y),
    });
  };

  const onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== pointerId) return;
    stopDragging();
  };

  const onPointerDown = (event: PointerEvent) => {
    // Only a primary press starts a drag; a right-click must stay a context menu.
    if (!enabled.value || event.button !== 0) return;

    dragging.value = true;
    pointerId = event.pointerId;
    pointerOrigin = { x: event.clientX, y: event.clientY };
    offsetOrigin = { ...offset.value };

    const handle = event.currentTarget;
    if (handle instanceof Element) {
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        // Without capture the window listeners still track the drag.
      }
    }
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (!enabled.value) return;
    const step = ARROW_KEY_STEPS[event.key];
    if (!step) return;
    event.preventDefault();
    nudge(step);
  };

  /**
   * Re-checks the current offset against the viewport. This also matters at
   * rest: the panel is anchored to the bottom, so anything that makes it taller
   * grows it upwards and can push its header off the top of the screen.
   */
  const clampToViewport = () => {
    const next = clamp(offset.value);
    if (next.x === offset.value.x && next.y === offset.value.y) return;
    offset.value = next;
  };

  // A panel that stops being draggable returns to its anchor, so it can never
  // stay stranded at an offset the user has no way to undo.
  watch(enabled, (isEnabled) => {
    if (!isEnabled) {
      stopDragging();
      reset();
    }
  });

  let resizeObserver: ResizeObserver | null = null;

  const observeElement = (target: HTMLElement | null) => {
    resizeObserver?.disconnect();
    if (target) resizeObserver?.observe(target);
  };

  watch(element, observeElement);

  onMounted(() => {
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(clampToViewport);
      observeElement(element.value);
    }
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    window.addEventListener("resize", clampToViewport);
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
    window.removeEventListener("resize", clampToViewport);
  });

  const style = computed(() =>
    offset.value.x === 0 && offset.value.y === 0
      ? {}
      : {
          transform: `translate(${offset.value.x}px, ${offset.value.y}px)`,
        },
  );

  return {
    offset,
    dragging,
    style,
    onPointerDown,
    onKeydown,
    reset,
    /** Re-clamps against the current viewport, e.g. after a size change. */
    reclamp: clampToViewport,
  };
};
